import { type TFilterWithViewAllRef } from '@/components';
import {
	ALERT_50,
	DivisionRecommenderEnum,
	MasterInfoEnum,
	ROUTERS,
} from '@/constant';
import {
	searchParamsKey,
	useCommonList,
	useCreateListSubmit,
	useRowSelection,
} from '@/hooks';
import { ApiResponseError, ICommonRecommender } from '@/services';
import {
	CommonActions,
	TCommonUpdateRecommenderBody,
	useAppSelector,
	WorkerFranchiseSelectors,
} from '@/stores';
import { MyPageSelectors } from '@/stores/my-page';
import { filter, insertObjectIfV2, NOOP, showAlert } from '@/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { TableRowSelection } from 'antd/es/table/interface';

import {
	getColumns,
	initialValues,
	PageTypeEnumSelectRecommender,
	TValues,
} from './select-recommender-modal.config';
import { DataType, IProps } from './select-recommender-modal.type';

export const useLogic = (props: IProps) => {
	const {
		open,
		providerTable,
		callBackFetchingData,
		onCancel,
		providerId,
		prevRecommendId,
	} = props;

	const { id } = useParams<{ id: string }>();

	const [params, setParams] = useState({
		...initialValues,
	});
	const dispatch = useDispatch();

	const {
		commonList: { fetching, data },
	} = useCommonList<ICommonRecommender>(
		MasterInfoEnum.COMMON_RECOMMENDER,
		open,
		params
	);

	const { isWorkerPublicAdminRole } = useSelector(
		MyPageSelectors.selectDataMyPage
	);

	const rowSelection = useRowSelection<TableRowSelection<DataType>, DataType>({
		getCheckboxProps: (record: DataType) => ({
			disabled:
				record.type === DivisionRecommenderEnum.OVERSEAS_LOCAL_GOVERNMENT &&
				providerTable === PageTypeEnumSelectRecommender.FRANCHISE,
		}),
	});

	const franchise = useAppSelector(WorkerFranchiseSelectors.selectDetail);

	const filterRef = useRef<TFilterWithViewAllRef>(null);

	const toCreatePathName = useMemo(() => {
		const queryProps = {
			franchiseName: franchise.name,
			franchiseId: franchise.id,
			spouseName: franchise.employerName,
			spousePhone: franchise.employerPhone,
			providerTable,
			...insertObjectIfV2(
				providerTable === PageTypeEnumSelectRecommender.APPLICANT,
				{
					applicantId: Number(id),
				}
			),
		};
		return `${ROUTERS.RM1_02}?${searchParamsKey}=${encodeURIComponent(JSON.stringify(queryProps))}`;
	}, [franchise, providerTable, searchParamsKey, id]);

	const { onCreateSubmit } = useCreateListSubmit({
		toCreatePathName,
	});

	const fetchData = useCallback((params: TValues) => {
		setParams((prev) => ({
			...prev,
			...params,
		}));
	}, []);

	const columns = useMemo(getColumns, []);

	const onSaveClick = useCallback(() => {
		dispatch(
			CommonActions.updateCommonRecommenderRequest({
				data: {
					recommendId: rowSelection.selectedRowKeys[0] as number,
					...insertObjectIfV2(
						providerTable === PageTypeEnumSelectRecommender.FRANCHISE,
						{
							franchiseId: Number(id),
							employerName: franchise?.employerName,
							employerPhone: franchise?.employerPhone,
						}
					),
					...insertObjectIfV2(
						providerTable === PageTypeEnumSelectRecommender.APPLICANT,
						{
							applicantId: Number(id),
							prevRecommendId: prevRecommendId,
						}
					),
				} as TCommonUpdateRecommenderBody,
				callback(error: ApiResponseError) {
					if (error) {
						return;
					}
					callBackFetchingData?.();
				},
			})
		);
		onCancel();
	}, [
		providerTable,
		rowSelection.selectedRowKeys,
		franchise?.employerName,
		franchise?.employerPhone,
		prevRecommendId,

		callBackFetchingData,
		onCancel,
	]);

	const onCheckIfExists = useCallback(() => {
		if (!rowSelection?.selectedRowKeys?.length) {
			showAlert('추천자을(를) 선택해주세요.');
			return;
		}

		if (providerTable === PageTypeEnumSelectRecommender.APPLICANT) {
			return onSaveClick();
		}

		const recommenderDataWithoutCurrentProvider = filter(
			rowSelection?.selectedRows,
			(item) => item?.franchiseId && Number(item.franchiseId) !== providerId
		);

		if (recommenderDataWithoutCurrentProvider?.length) {
			showAlert(
				ALERT_50(
					recommenderDataWithoutCurrentProvider
						.map((item) => item.name)
						.join(', '),
					franchise?.employerName
				),
				onSaveClick,
				NOOP
			);
		} else {
			onSaveClick();
		}
	}, [
		onSaveClick,
		onCancel,
		franchise?.employerName,
		rowSelection?.selectedRows,
		providerId,
		providerTable,
	]);

	const onCreateSuccess = useCallback(() => {
		callBackFetchingData?.();
	}, []);

	const onNavigateRecommender = useCallback(() => {
		onCreateSubmit?.({
			callbackSuccess: onCreateSuccess,
		});
	}, [onCreateSubmit, onCreateSuccess]);

	useEffect(() => {
		if (!open) {
			filterRef?.current?.onReset?.(true);
		}
	}, [open, filterRef]);

	return {
		filterRef,
		isWorkerPublicAdminRole,
		initialValues,
		fetchData,
		fetching,
		columns,
		dataSource: data,
		rowSelection,
		onCheckIfExists,
		onNavigateRecommender,
	};
};
